import { useState } from 'react'
import { message } from 'antd'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { TransferKey } from 'antd/es/transfer/interface'
import { Account } from '@/widgets/accountsGrid'
import { routes } from '@/shared/config/routes'
import { useAuthStore } from '@/app/store/authStore'
import { useSystemStore } from '@/app/store/systemStore'
import { getSystemDisplayName } from '@/shared/lib/getSystemDisplayName'
import { buildReceiverHostUrl } from '@/shared/lib/buildReceiverHostUrl'
import authAxios from '@/shared/api/authAxios'
import {
  addOptimisticTask,
  syncTaskInCache,
} from '@/entities/task/model/tasksCache'
import { tasksKeys } from '@/entities/task/model/tasksKeys'
import { fetchTaskById } from '@/entities/task/api/tasksApi'
import { transformApiTaskToTaskItem } from '@/entities/task/lib/transformTask'
import {
  getDefaultTaskItem,
  TASK_STATUSES,
  TaskItem,
  TASK_TYPES,
} from '@/entities/task/model/taskTypes'
import { TRANSFER_MODES, TransferMode } from './transferTypes'

export interface UseStartTransferParams {
  transferMode: TransferMode
  isMyAccountSelected: boolean
  selectedAccount: string
  targetAgentAccounts: Account[]
  selectedSubpartner: string
  accountsData: any[]
  subPartners: any[]
  accountsTargetKeys: TransferKey[]
  usersTargetKeys: TransferKey[]
  objectsTargetKeys: TransferKey[]
  driversTargetKeys: TransferKey[]
  geozonesTargetKeys: TransferKey[]
  retranslationsTargetKeys: TransferKey[]
  loadMessages: boolean
  messagePeriod: number
  loadMessagesFull: boolean
  messagePeriodFull: number
  loadDriversCodes: boolean
  systemUrl: string
  buildSourceAuth: () => { auth: string | null }
  // Доп. коды подзадач для конкретной системы (например GS_SPECIFIC для полного переноса glonassoft)
  extraSubTaskCodes?: number[]
  // Переопределение имени аккаунта-источника в списке задач (например для glonassoft FULL со списком агентов)
  getUploadAccountName?: (agentIds: string[]) => string
  // свой sourceId
  sourceId?: string
}

export const useStartTransfer = ({
  transferMode,
  isMyAccountSelected,
  selectedAccount,
  targetAgentAccounts,
  selectedSubpartner,
  accountsData,
  subPartners,
  accountsTargetKeys,
  usersTargetKeys,
  objectsTargetKeys,
  driversTargetKeys,
  geozonesTargetKeys,
  retranslationsTargetKeys,
  loadMessages,
  messagePeriod,
  loadMessagesFull,
  messagePeriodFull,
  loadDriversCodes,
  systemUrl,
  buildSourceAuth,
  extraSubTaskCodes = [],
  getUploadAccountName,
  sourceId,
}: UseStartTransferParams) => {
  const [isTransferring, setIsTransferring] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user, userPassword, tokenAccount, currentServer } = useAuthStore()
  const { selectedSystem } = useSystemStore()

  const handleStartTransfer = async () => {
    setIsTransferring(true)

    try {
      let agentId = ''
      let selectedAccountName = 'GlonassSoft'
      const baseTasks = [...TASK_TYPES.BASE]
      const driverCodeTasks = loadDriversCodes
        ? [...TASK_TYPES.DRIVER_CODES]
        : []

      const subTasks = [
        ...baseTasks,
        ...driverCodeTasks,
        ...extraSubTaskCodes,
      ].sort((a, b) => a - b)

      if (isMyAccountSelected) {
        agentId = user?.id || ''
        selectedAccountName = user?.username || 'GlonassSoft'
      } else {
        const selectedAgentAccount = targetAgentAccounts.find(
          (account) => account.id === selectedAccount
        )
        agentId = selectedAccount || ''
        selectedAccountName = selectedAgentAccount?.title || 'GlonassSoft'
      }

      let sourceAccount = tokenAccount
      const sourceAccountId = sourceId || selectedSubpartner

      const foundAccount = accountsData.find(
        (acc) => acc.id === sourceAccountId
      )

      if (foundAccount) {
        sourceAccount = foundAccount.name
      }

      let finalMessagePeriod = 0

      if (transferMode === TRANSFER_MODES.ENTITIES) {
        finalMessagePeriod = loadMessages ? messagePeriod : 0
      } else {
        finalMessagePeriod = loadMessagesFull ? messagePeriodFull : 0
      }

      const isParentAccount = !subPartners.some(
        (partner) => partner.id === sourceAccountId
      )

      let agentIds: string[] = []

      if (transferMode === TRANSFER_MODES.ENTITIES) {
        agentIds = isParentAccount || !sourceAccountId ? [] : [sourceAccountId]
      } else if (transferMode === TRANSFER_MODES.FULL) {
        agentIds = accountsTargetKeys as string[]
      }

      if (sourceAccountId && !isParentAccount) {
        if (!agentIds.includes(sourceAccountId)) {
          agentIds = [...agentIds, sourceAccountId]
        }
      }

      const sourceAuth = buildSourceAuth()

      const requestData = {
        name: `Перенос данных из ${sourceAccount} в ${selectedAccountName}`,
        description: `Перенос данных из ${getSystemDisplayName(selectedSystem)} в ГЛОНАССSoft. Режим: ${transferMode === TRANSFER_MODES.ENTITIES ? 'Только сущности' : 'Полный перенос'}`,
        source: {
          id: sourceId || tokenAccount,
          name: sourceAccount,
          system: selectedSystem,
          host: systemUrl,
        },
        receiver: {
          id: user?.username,
          name: selectedAccountName,
          system: '',
          host: buildReceiverHostUrl(currentServer),
        },
        messagePeriod: finalMessagePeriod,
        subTasks,
        sourceFilter: {
          agentIds:
            transferMode === TRANSFER_MODES.ENTITIES && isParentAccount
              ? null
              : agentIds,
          userIds:
            transferMode === TRANSFER_MODES.ENTITIES
              ? (usersTargetKeys as string[])
              : transferMode === TRANSFER_MODES.FULL
                ? null
                : [],
          vehicleIds:
            transferMode === TRANSFER_MODES.ENTITIES
              ? (objectsTargetKeys as string[])
              : transferMode === TRANSFER_MODES.FULL
                ? null
                : [],
          driverIds:
            transferMode === TRANSFER_MODES.ENTITIES
              ? (driversTargetKeys as string[])
              : transferMode === TRANSFER_MODES.FULL
                ? null
                : [],
          zoneIds:
            transferMode === TRANSFER_MODES.ENTITIES
              ? (geozonesTargetKeys as string[])
              : transferMode === TRANSFER_MODES.FULL
                ? null
                : [],
          retranslationIds:
            transferMode === TRANSFER_MODES.ENTITIES
              ? (retranslationsTargetKeys as string[])
              : transferMode === TRANSFER_MODES.FULL
                ? null
                : [],
        },
        sourceAuth,
        receiverAuth: {
          loginPassword: {
            login: user?.username || '',
            password: userPassword || '',
          },
          agentId: agentId,
          useAsRoot: transferMode === TRANSFER_MODES.ENTITIES,
        },
      }

      const response = await authAxios.post('/public/Tasks', requestData, {
        timeout: 30000,
      })

      if (response.status === 200 || response.status === 201) {
        const taskId = response.data

        const uploadAccountName = getUploadAccountName
          ? getUploadAccountName(agentIds)
          : tokenAccount || getSystemDisplayName(selectedSystem)

        const newTask: TaskItem = {
          ...getDefaultTaskItem(),
          id: taskId,
          status: TASK_STATUSES.CREATED,
          createdAt: new Date().toLocaleString('ru-RU'),
          startedAt: 'Не начата',
          completedAt: 'Не завершена',
          uploadAccount: uploadAccountName,
          downloadAccount: selectedAccountName,
        }

        if (user?.username) {
          addOptimisticTask(queryClient, tasksKeys.my(user.username), newTask)
        }

        try {
          const taskResponse = await fetchTaskById(taskId)
          const transformedTask = transformApiTaskToTaskItem(taskResponse)

          if (transformedTask && user?.username) {
            syncTaskInCache(
              queryClient,
              tasksKeys.my(user.username),
              transformedTask
            )
          }
        } catch (loadError) {
          console.error('❌ Ошибка при подтверждении задачи:', loadError)
        }

        navigate(routes.tasks)
      } else {
        message.warning(
          `Перенос данных запущен со статусом: ${response.status}`
        )
      }
    } catch (error) {
      console.error('❌ Ошибка при запуске переноса данных:', error)

      if (axios.isAxiosError(error)) {
        console.error('🔴 Детали ошибки:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
        })

        if (error.response?.data) {
          message.error(
            `Ошибка сервера: ${JSON.stringify(error.response.data)}`
          )
        } else if (error.code === 'ECONNABORTED') {
          message.error('Таймаут запроса. Сервер не отвечает')
        } else {
          message.error('Ошибка сети при запуске переноса данных')
        }
      } else {
        message.error('Неизвестная ошибка при запуске переноса данных')
      }
    } finally {
      setIsTransferring(false)
    }
  }

  return { handleStartTransfer, isTransferring }
}
